import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import PlanStaffingHoverMenu from "components/Plans/PlanStaffingHoverMenu";

import { PLAN_GRID_VIEWS } from "components/Plans";
import {
	buildQuarterHourCoverageMap,
	getEffectiveNeeded,
	getFractionalNeedCoverage,
	getStaffingAssignmentIntervals,
	isStaffingOverwrite,
} from "components/Plans/planStaffingNeedsUtils";
import StaffingDayHeader from "components/Plans/staffing/StaffingDayHeader";
import NeedNameCell from "components/Plans/staffing/NeedNameCell";
import CoverageBox from "components/Plans/staffing/CoverageBox";
import { getPeopleTooltipList } from "components/Plans/staffing/staffingTooltip";
import { useStaffingTableInteractions } from "components/Plans/staffing/useStaffingTableInteractions";

/**
 * Tabla de necesidades FRACCIONADAS para la vista de Funciones.
 *
 * A diferencia de PlanStaffingLevels (que muestra el valor `needed`), esta tabla
 * muestra la COBERTURA `current/needed` por día, calculando `current` desde la
 * planificación real (plan_assignments) con la misma lógica que la vista de
 * Horas. Solo se debe renderizar para periodos de ≤ 7 días.
 */
const getTableClass = (planGridView) => {
	if (planGridView === PLAN_GRID_VIEWS.ubications) return "table-ubications";
	if (planGridView === PLAN_GRID_VIEWS.default) return "table-default";
	if (planGridView === PLAN_GRID_VIEWS.grid) return "table-grid";
	return "";
};

const FractionalCoverageCell = ({
	coverage,
	needed,
	counter,
	row,
	index,
	hoveredCellId,
	tooltipCellId,
	handleCellMouseEnter,
	handleCellMouseLeave,
	handleNeedSelection,
	onEdit,
	onBringEmployees,
}) => {
	const checked = counter.checked || false;
	const isEmpty = needed === 0 && coverage.current === 0;

	// Si no se puede editar, una celda vacía (0/0) no aporta nada y se deja en
	// blanco. Cuando la edición está disponible, se mantiene interactiva para
	// poder crear/editar la necesidad aunque required sea 0.
	if (isEmpty && !onEdit) {
		return <td className="need-cell"></td>;
	}

	const cellKey = `${row}-${index}`;
	const isHovered = hoveredCellId === cellKey;
	const showTooltipDelayed = tooltipCellId === cellKey;

	const tooltip = (
		<Tooltip className="tooltip-staffing">
			{coverage.current}/{needed}
			{showTooltipDelayed && getPeopleTooltipList(coverage.people)}
		</Tooltip>
	);

	return (
		<OverlayTrigger
			placement="top"
			overlay={tooltip}
			show={showTooltipDelayed && !isEmpty}
			trigger={[]}
		>
			<td
				className={`need-cell need-cell-coverage${
					isEmpty ? " need-cell-empty" : ""
				}${handleNeedSelection ? " btn-checkbox" : ""}${
					checked ? " active" : ""
				}`}
				data-cell-key={cellKey}
				onMouseEnter={() => handleCellMouseEnter(cellKey)}
				onMouseLeave={(e) => handleCellMouseLeave(e, cellKey)}
				style={{ position: "relative" }}
			>
				<div className="react-contextmenu-wrapper">
					{handleNeedSelection && (
						<>
							<input
								type="checkbox"
								id={`need_${row}_${index}`}
								onChange={(e) =>
									handleNeedSelection(row, index, false, e, {
										current: coverage.current,
									})
								}
								checked={checked}
							/>
							<label
								htmlFor={`need_${row}_${index}`}
								className="btn-check-label"
								draggable
								onDragEnter={() =>
									handleNeedSelection(
										row,
										index,
										true,
										{ target: { checked: true } },
										{ current: coverage.current }
									)
								}
							></label>
						</>
					)}
					{!isEmpty && (
						<CoverageBox
							current={coverage.current}
							needed={needed}
							isEdited={isStaffingOverwrite(counter)}
						/>
					)}
				</div>

				{isHovered && (onEdit || onBringEmployees) && (
					<PlanStaffingHoverMenu
						itemStaffing={counter}
						row={row}
						index={index}
						cellKey={cellKey}
						onMouseEnter={() => handleCellMouseEnter(cellKey)}
						onMouseLeave={(e) => handleCellMouseLeave(e, cellKey)}
						onBringEmployees={onBringEmployees}
						onEdit={
							onEdit
								? (itemStaffing, row, index) =>
										onEdit(itemStaffing, row, index, coverage.current)
								: undefined
						}
					/>
				)}
			</td>
		</OverlayTrigger>
	);
};

const FractionalNeedRow = ({
	assignment,
	quarterHourMap,
	selectedDay,
	assignmentRowIndex,
	hoveredCellId,
	tooltipCellId,
	handleCellMouseEnter,
	handleCellMouseLeave,
	handleNeedSelection,
	onEdit,
	onBringEmployees,
}) => {
	const functionName = assignment.function.name;
	const timeIntervals = getStaffingAssignmentIntervals(assignment);

	const counterForSelectedDay = assignment.counters.find((counter) =>
		moment(counter.day).isSame(selectedDay, "day")
	);
	const neededSelected = counterForSelectedDay
		? getEffectiveNeeded(counterForSelectedDay)
		: 0;
	const coverageSelected = counterForSelectedDay
		? getFractionalNeedCoverage({
				quarterHourMap,
				dayKey: counterForSelectedDay.day,
				functionName,
				timeIntervals,
		  })
		: { current: 0, people: [] };
	const isHidden =
		selectedDay && neededSelected === 0 && coverageSelected.current === 0;

	return (
		<tr className={isHidden ? "hidden" : ""}>
			<NeedNameCell assignment={assignment} />
			{assignment.counters.map((counter, i) => {
				if (selectedDay && !moment(counter.day).isSame(selectedDay, "day")) {
					return <td key={i} className="need-cell"></td>;
				}

				const needed = getEffectiveNeeded(counter);
				const coverage = getFractionalNeedCoverage({
					quarterHourMap,
					dayKey: counter.day,
					functionName,
					timeIntervals,
				});

				const itemStaffing = { ...counter };
				itemStaffing.function = assignment.function;
				itemStaffing.shift = assignment.shift;
				if (assignment.time_interval) {
					itemStaffing.time_interval = assignment.time_interval;
				}
				if (assignment.ubication) itemStaffing.ubication = assignment.ubication;

				return (
					<FractionalCoverageCell
						key={i}
						coverage={coverage}
						needed={needed}
						counter={itemStaffing}
						row={assignmentRowIndex}
						index={i}
						hoveredCellId={hoveredCellId}
						tooltipCellId={tooltipCellId}
						handleCellMouseEnter={handleCellMouseEnter}
						handleCellMouseLeave={handleCellMouseLeave}
						handleNeedSelection={handleNeedSelection}
						onEdit={assignment.shift ? onEdit : undefined}
						onBringEmployees={onBringEmployees}
					/>
				);
			})}
		</tr>
	);
};

const PlanStaffingLevelsFractional = (props) => {
	const {
		plan_days,
		plan_grid_view,
		plan_assignments,
		staffing_level_assignments,
		staffing_level_current_assignments,
		openNewStaffingLevelModal,
		handleNeedSelection,
		handleEditStaffingLevel,
		handleBringEmployeesTemporarily,
		getAssignmentRowIndex,
	} = props;
	const { t } = useTranslation();

	const {
		selectedDay,
		hoveredCellId,
		tooltipCellId,
		hoveredHeaderDay,
		handleDayClick,
		handleHeaderMouseEnter,
		handleHeaderMouseLeave,
		handleCellMouseEnter,
		handleCellMouseLeave,
	} = useStaffingTableInteractions();

	const quarterHourMap = useMemo(
		() => buildQuarterHourCoverageMap(plan_assignments),
		[plan_assignments]
	);

	const hasNeeds = staffing_level_assignments.length > 0;
	const hasCurrent = staffing_level_current_assignments.length > 0;

	if (!hasNeeds && !hasCurrent) return null;

	// Fallback: si no hay necesidades fraccionadas pero sí planificación previa,
	// mostramos esas filas (mismo criterio que PlanStaffingLevels).
	const rowsAssignments = hasNeeds
		? staffing_level_assignments
		: staffing_level_current_assignments;

	const handleHeaderAddClick = (itemStaffingHeader) => {
		if (openNewStaffingLevelModal) openNewStaffingLevelModal(itemStaffingHeader);
		handleHeaderMouseLeave({});
	};

	const handleEdit = (itemStaffing, row, index, computedCurrent) => {
		if (handleNeedSelection) {
			const overrides = { current: computedCurrent };
			// Si la celda ya está seleccionada (p. ej. edición múltiple), no
			// reemplazar el resto de la selección de la misma fila.
			if (!itemStaffing.checked) {
				overrides.replace = true;
			}
			handleNeedSelection(
				row,
				index,
				false,
				{ target: { checked: true } },
				overrides
			);
		}
		if (handleEditStaffingLevel) {
			setTimeout(() => handleEditStaffingLevel(itemStaffing), 50);
		}
	};

	const handleBring = (itemStaffing, row, index) => {
		if (handleNeedSelection) {
			const overrides = {};
			if (!itemStaffing.checked) overrides.replace = true;
			handleNeedSelection(
				row,
				index,
				false,
				{ target: { checked: true } },
				overrides
			);
		}
		if (handleBringEmployeesTemporarily) {
			handleBringEmployeesTemporarily(itemStaffing);
		}
	};

	const canEdit = hasNeeds && handleEditStaffingLevel && getAssignmentRowIndex;
	const canBring =
		hasNeeds && handleBringEmployeesTemporarily && getAssignmentRowIndex;
	const canSelect = hasNeeds && handleNeedSelection && getAssignmentRowIndex;

	return (
		<table
			id="tableNeedsFractional"
			className={`table table-bordered table-plan table-needs table-needs-fractional ${getTableClass(
				plan_grid_view
			)}`}
		>
			<thead>
				<tr>
					<th className="main-col z-30">
						<div className="th-wrap">{t("staffingLevels.coverage")}</div>
					</th>
					<StaffingDayHeader
						planDays={plan_days}
						planGridView={plan_grid_view}
						selectedDay={selectedDay}
						hoveredHeaderDay={hoveredHeaderDay}
						onDayClick={handleDayClick}
						onHeaderMouseEnter={handleHeaderMouseEnter}
						onHeaderMouseLeave={handleHeaderMouseLeave}
						onHeaderAddClick={openNewStaffingLevelModal ? handleHeaderAddClick : null}
						t={t}
					/>
				</tr>
			</thead>
			<tbody>
				{rowsAssignments.map((assignment, i) => (
					<FractionalNeedRow
						key={i}
						assignment={assignment}
						quarterHourMap={quarterHourMap}
						selectedDay={selectedDay}
						assignmentRowIndex={
							canEdit ? getAssignmentRowIndex(assignment) : i
						}
						hoveredCellId={hoveredCellId}
						tooltipCellId={tooltipCellId}
						handleCellMouseEnter={handleCellMouseEnter}
						handleCellMouseLeave={handleCellMouseLeave}
						handleNeedSelection={canSelect ? handleNeedSelection : null}
						onEdit={canEdit ? handleEdit : null}
						onBringEmployees={canBring ? handleBring : null}
					/>
				))}
			</tbody>
		</table>
	);
};

export default PlanStaffingLevelsFractional;
